import React, { useEffect, useState } from "react";
import { TableColumn, TableRow, ToxnotReactTable } from "../ToxnotReactTable";
import { ToxnotReactModal, ToxnotReactModalBody, ToxnotReactModalFooter } from "../ToxnotReactModal";
import { TopActionButtons } from "../TopActionsAndScrollableContentContainer";
import TextInput from "../TextInput";

interface ProductListProps {
    value: string;
    localizer: any;
    onValueChange: (value: string) => void;
}

interface Product {
    ProductNumber: string;
    ProductName?: string;
    Comments?: string;
}

const ProductList: React.FC<ProductListProps> = ({ value, localizer, onValueChange }) => {
    const emptyProduct: Product = { ProductNumber: "", ProductName: "", Comments: "" };
    const [products, setProducts] = useState<Product[]>([]);
    const [tableItemId, setTableItemId] = useState<string>("");
    const [product, setProduct] = useState<Product>(emptyProduct);
    const [productIdsToDelete, setProductIdsToDelete] = useState<{ [key: string]: string }>({});

    useEffect(() => {
        const parsedProducts = JSON.parse(value && value.trim() !== "" ? value : "[]");
        setProducts(parsedProducts ?? []);
    }, [value]);

    const getProducts = () => products;

    const getProductListHeaderRow = (): TableColumn<Product>[] => [
        { value: localizer.localizeString("Product Number") },
        { value: localizer.localizeString("Product Name") },
        { value: localizer.localizeString("Comments") },
    ];

    const getProductListRow = (product: Product): TableRow => ({
        cols: [
            product.ProductNumber ?? localizer.localizeString("Unknown Product Number"),
            product.ProductName ?? localizer.localizeString("Unknown Product Name"),
            product.Comments ?? localizer.localizeString("Unknown Comments"),
        ],
    });

    const handleProductRowClick = (e: React.MouseEvent, product: Product): void => {
        setProduct(product);
        setTableItemId(product.ProductNumber);
        $("#product-details-modal-v2").modal("show");
    };

    const onTableInputValueChange = (fieldName: string, newValue: string) => {
        setProduct(prevProduct => ({ ...prevProduct, [fieldName]: newValue }));
    };

    const renderProductDetailsTableHeaderRow = () => (
        <thead>
            <tr>
                <th>{localizer.localizeString("Product Detail")}</th>
                <th>{localizer.localizeString("Value")}</th>
            </tr>
        </thead>
    );

    const renderProductDetailsTableBody = () => (
        <tbody>
            {Object.entries(product ?? {}).map(([key, value]) => (
                <tr key={key}>
                    <td>
                        <b>{localizer.localizeString(key === "ProductNumber" ? `${localizer.localizeString("Product Number")} (*)` : key)}</b>
                    </td>
                    <td>
                        <TextInput
                            value={value ?? ""}
                            onValueChange={(newValue) => onTableInputValueChange(key, newValue)}
                            additionalClass={key === "ProductNumber" && value === "" ? "highlighted-input" : ""}
                        />
                    </td>
                </tr>
            ))}
        </tbody>
    );

    const createProductButton_click = () => {
        setProduct(emptyProduct);
        $("#product-details-modal-v2").modal("show");
    };

    const onModalCancel = () => {
        setProduct(emptyProduct);
    };

    const onModalSave = () => {
        if (!product.ProductNumber) return;

        setProducts(prevProducts => {
            let updatedProducts = [...prevProducts];

            const existingIndex = updatedProducts.findIndex(p => p.ProductNumber === tableItemId);

            if (existingIndex > -1) {
                updatedProducts[existingIndex] = product;
            } else {
                updatedProducts.push(product);
            }

            onValueChange(JSON.stringify(updatedProducts));
            return updatedProducts;
        });

        setTableItemId("");
        setProduct(emptyProduct);
        $("#product-details-modal-v2").modal("hide");
    };

    const getProductNumber = (model: Product) => model.ProductNumber;

    const updateSelectedProducts = (selectedIds: { [id: string]: string }) => {
        setProductIdsToDelete({ ...selectedIds });
    };

    const deleteProductButton_click = (e: React.MouseEvent) => {
        e.nativeEvent.stopImmediatePropagation();
        // Removed alert check - button will be disabled instead
        $("#warn-about-deleting-products-modal-v2").modal("show");
    };

    const closeDeleteProductsWarningModal_click = (e: React.MouseEvent) => {
        e.nativeEvent.stopImmediatePropagation();
        $("#warn-about-deleting-products-modal-v2").modal("hide");
    };

    const deleteProductsButtonConfirm_click = () => {
        setProducts(prevProducts => {
            const selectedIds = new Set(Object.keys(productIdsToDelete));
            const updatedProducts = prevProducts.filter(p => !selectedIds.has(p.ProductNumber));

            onValueChange(JSON.stringify(updatedProducts));
            return updatedProducts;
        });

        setProductIdsToDelete({});
        $("#warn-about-deleting-products-modal-v2").modal("hide");
    };

    return (
        <div>
            <h4><strong>{localizer.localizeString("Product List")}</strong></h4>
            <TopActionButtons>
                <div>
                    <button className="btn btn-primary" onClick={createProductButton_click}>
                        <span className="glyphicon glyphicon-plus" /> {localizer.localizeString("Add Product")}
                    </button>
                    <button  className="btn btn-danger" onClick={deleteProductButton_click} disabled={Object.keys(productIdsToDelete).length === 0}>
                        <span className="glyphicon glyphicon-remove" />  {localizer.localizeString("Delete Product")}
                    </button>
                </div>
            </TopActionButtons>

            <div id="product-list-table-v2" className="tn-section-padding-container">
                <ToxnotReactTable
                    allRowModels={getProducts()}
                    localizer={localizer}
                    headerRow={getProductListHeaderRow()}
                    getRowForTable={getProductListRow}
                    rowClickEventHandler={handleProductRowClick}
                    selectingRowOptions={{
                        rowModelGetId: getProductNumber,
                        onRowSelected: updateSelectedProducts
                    }}
                    enablePaging={true}
                    fullSectionWidth={true}
                />
            </div>

            <ToxnotReactModal modalId="product-details-modal-v2" localizedTitle={localizer.localizeString("Product Details")}>
                <ToxnotReactModalBody>
                    <table id="createTableProduct" className="table table-bordered">
                        {renderProductDetailsTableHeaderRow()}
                        {renderProductDetailsTableBody()}
                    </table>
                </ToxnotReactModalBody>
                <ToxnotReactModalFooter localizer={localizer} onCancel={onModalCancel}>
                    <button className="btn btn-toxnot-primary" onClick={onModalSave} disabled={!product.ProductNumber?.length}>
                        {localizer.localizeString("Save")}
                    </button>
                </ToxnotReactModalFooter>
            </ToxnotReactModal>

            <ToxnotReactModal modalId="warn-about-deleting-products-modal-v2" localizedTitle={localizer.localizeString("Are you sure you want to delete?")}>
                <ToxnotReactModalBody>
                    <p>{localizer.localizeString("You are about to delete product(s). This will remove them from the Exchange.")}</p>
                </ToxnotReactModalBody>
                <ToxnotReactModalFooter localizer={localizer} enabledCloseButton={false}>
                    <button className="btn btn-default" onClick={closeDeleteProductsWarningModal_click}>{localizer.localizeString("Cancel")}</button>
                    <button className="btn btn-danger" onClick={deleteProductsButtonConfirm_click}>{localizer.localizeString("Yes, Delete")}</button>
                </ToxnotReactModalFooter>
            </ToxnotReactModal>
        </div>
    );
};

export default ProductList;
